import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UploadSimple, FileZip, CheckCircle, WarningCircle, CircleNotch, Eye, Copy, X } from '@phosphor-icons/react';

const formatDoUrl = (url) => {
  if (!url) return '';
  let u = url.startsWith('http') ? url : 'https://' + url;
  if (u.endsWith('/')) u += 'index.html';
  else if (!u.endsWith('/index.html')) u += '/index.html';
  return u;
};

const NewDeployment = () => {
  const [activeTab, setActiveTab] = useState('direct'); // 'direct' | 'saved'
  const [savedSites, setSavedSites] = useState([]);

  // DO account selection (optional pin, like Azure)
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  
  // Direct Upload State
  const [linksCount, setLinksCount] = useState(1);
  const [slots, setSlots] = useState([{ file: null, status: 'idle', progress: 0, error: null, url: null, id: null }]);
  const [deployingDirect, setDeployingDirect] = useState(false);

  // Saved Sites State
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [savedDeployCount, setSavedDeployCount] = useState(1);
  const [savedDeployments, setSavedDeployments] = useState([]);
  const [deployingSaved, setDeployingSaved] = useState(false);

  // Completion modal
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [modalDeps, setModalDeps] = useState([]);

  useEffect(() => {
    axios.get(`/api/accounts`, { withCredentials: true })
      .then(res => setAccounts((res.data || []).filter(a => a.status === 'active')))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (activeTab === 'saved') {
      axios.get(`/api/saved-sites`, { withCredentials: true })
        .then(res => setSavedSites(res.data))
        .catch(() => toast.error("Failed to load saved sites"));
    }
  }, [activeTab]);

  useEffect(() => {
    setSlots(prev => {
      const newSlots = [...prev];
      while (newSlots.length < linksCount) {
        newSlots.push({ file: null, status: 'idle', progress: 0, error: null, url: null, id: null });
      }
      return newSlots.slice(0, linksCount);
    });
  }, [linksCount]);

  const updateSlot = (index, updates) => {
    setSlots(prev => {
      const arr = [...prev];
      arr[index] = { ...arr[index], ...updates };
      return arr;
    });
  };

  const handleFileChange = (index, file) => {
    if (file && !file.name.endsWith('.zip')) {
      toast.error("File must be a .zip archive");
      return;
    }
    updateSlot(index, { file, status: 'idle', progress: 0, error: null, url: null });
  };

  const handleDeployDirect = async () => {
    if (slots.some(s => !s.file)) {
      return toast.error("Please select a zip file for all slots.");
    }
    setDeployingDirect(true);
    setShowCompletionModal(false);

    const promises = slots.map(async (slot, index) => {
      if (['live', 'uploading', 'extracting'].includes(slot.status)) return;
      updateSlot(index, { status: 'initializing', progress: 0, error: null });
      
      try {
        const initRes = await axios.post(`/api/deployments/init`, {
          filename: slot.file.name,
          size_bytes: slot.file.size,
          account_id: selectedAccountId || null
        }, { withCredentials: true });
        
        const { presigned_url, deployment_id } = initRes.data;
        updateSlot(index, { status: 'uploading', id: deployment_id });
        
        await axios.put(presigned_url, slot.file, {
          headers: { 'Content-Type': 'application/zip' },
          onUploadProgress: (p) => {
            if (p.total) {
              const percent = Math.round((p.loaded * 100) / p.total);
              updateSlot(index, { progress: percent });
            }
          }
        });
        
        updateSlot(index, { status: 'extracting', progress: 100 });
        await axios.post(`/api/deployments/${deployment_id}/finalize`, {}, { withCredentials: true });
        
        let errCount = 0;
        const poll = async () => {
          try {
            const depRes = await axios.get(`/api/deployments/${deployment_id}`, { withCredentials: true, timeout: 20000 });
            errCount = 0;
            if (depRes.data.status === 'live') {
              updateSlot(index, { status: 'live', url: depRes.data.public_url });
            } else if (depRes.data.status === 'failed') {
              updateSlot(index, { status: 'failed', error: depRes.data.error_msg || "Extraction failed" });
            } else {
              setTimeout(poll, 2500);
            }
          } catch (err) {
            errCount += 1;
            if (errCount >= 15) {
              updateSlot(index, { status: 'failed', error: "Failed to poll status" });
            } else {
              setTimeout(poll, 2500);
            }
          }
        };
        poll();
      } catch (err) {
        const errorDetail = err.response?.data?.detail || err.message;
        const msg = Array.isArray(errorDetail) ? errorDetail[0]?.msg : (typeof errorDetail === 'string' ? errorDetail : JSON.stringify(errorDetail));
        updateSlot(index, { status: 'failed', error: msg });
      }
    });

    await Promise.allSettled(promises);
  };

  // Show completion modal when the direct-upload batch finishes
  useEffect(() => {
    if (deployingDirect) {
      const active = slots.filter(s => s.file);
      const allDone = active.length > 0 && active.every(s => s.status === 'live' || s.status === 'failed');
      if (allDone) {
        setDeployingDirect(false);
        setModalDeps(active.map(s => ({ status: s.status, url: s.url, label: s.file?.name })));
        setShowCompletionModal(true);
      }
    }
  }, [slots, deployingDirect]);

  const handleCopyAll = (deps) => {
    const list = deps || modalDeps;
    const urls = list.filter(d => d.status === 'live' && d.url).map(d => formatDoUrl(d.url)).join('\n');
    if (urls) {
      navigator.clipboard.writeText(urls);
      toast.success("All Live URLs copied to clipboard!");
    } else {
      toast.error("No Live URLs to copy.");
    }
  };

  const handleDeploySaved = async () => {
    if (!selectedSiteId) return toast.error("Please select a saved site.");
    setDeployingSaved(true);

    try {
      const res = await axios.post(`/api/deployments/from-saved`, {
        saved_site_id: selectedSiteId,
        count: savedDeployCount,
        account_id: selectedAccountId || null
      }, { withCredentials: true });

      const initialDeployments = res.data.map(d => ({ ...d, url: null, error: null }));
      setSavedDeployments(initialDeployments);

      // Poll each deployment (resilient: tolerate transient network blips)
      initialDeployments.forEach((dep, index) => {
        let errCount = 0;
        const poll = async () => {
          try {
            const pollRes = await axios.get(`/api/deployments/${dep.id}`, { withCredentials: true, timeout: 20000 });
            errCount = 0;
            setSavedDeployments(prev => {
              const arr = [...prev];
              if (pollRes.data.status === 'live') {
                arr[index] = { ...arr[index], status: 'live', url: pollRes.data.public_url };
              } else if (pollRes.data.status === 'failed') {
                arr[index] = { ...arr[index], status: 'failed', error: pollRes.data.error_msg || "Failed" };
              }
              return arr;
            });

            if (pollRes.data.status !== 'live' && pollRes.data.status !== 'failed') {
              setTimeout(poll, 3000);
            }
          } catch (err) {
            errCount += 1;
            // A single hiccup shouldn't kill a deploy that's still running server-side.
            if (errCount >= 15) {
              setSavedDeployments(prev => {
                const arr = [...prev];
                arr[index] = { ...arr[index], status: 'failed', error: "Polling error" };
                return arr;
              });
            } else {
              setTimeout(poll, 3000);
            }
          }
        };
        poll();
      });

    } catch (e) {
      const errorDetail = e.response?.data?.detail;
      const msg = Array.isArray(errorDetail) ? errorDetail[0]?.msg : (typeof errorDetail === 'string' ? errorDetail : "Failed to trigger deployments");
      toast.error(msg);
      setDeployingSaved(false);
    }
  };

  // Show completion modal when the saved-sites batch finishes
  useEffect(() => {
    if (deployingSaved && savedDeployments.length > 0) {
      const allDone = savedDeployments.every(d => d.status === 'live' || d.status === 'failed');
      if (allDone) {
        setDeployingSaved(false);
        setModalDeps(savedDeployments.map(d => ({ status: d.status, url: d.url, label: d.bucket_name })));
        setShowCompletionModal(true);
      }
    }
  }, [savedDeployments, deployingSaved]);

  const getStatusDisplay = (status) => {
    switch (status) {
      case 'initializing': return <span className="text-yellow-400 text-xs font-bold animate-pulse">Initializing...</span>;
      case 'uploading': return <span className="text-primary text-xs font-bold animate-pulse">Uploading...</span>;
      case 'extracting': return <span className="text-yellow-400 text-xs font-bold animate-pulse flex items-center gap-1"><CircleNotch className="animate-spin" /> Extracting & Publishing</span>;
      case 'live': return <span className="text-[#39FF14] text-xs font-bold flex items-center gap-1"><CheckCircle /> Live</span>;
      case 'failed': return <span className="text-red-500 text-xs font-bold flex items-center gap-1"><WarningCircle /> Failed</span>;
      default: return null;
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div>
        <h1 className="text-4xl font-black mb-2">NEW DEPLOYMENT</h1>
        <p className="text-slate-400">Launch buckets using a new zip or saved assets.</p>
      </div>

      <div className="bg-card border border-white/10 p-6 max-w-xl">
        <label className="text-xs uppercase tracking-widest text-slate-500 font-bold block mb-2">DigitalOcean Account</label>
        <select
          data-testid="do-account-select"
          value={selectedAccountId}
          onChange={(e) => setSelectedAccountId(e.target.value)}
          className="w-full bg-[#0A0B0E] border border-white/10 p-3 text-white focus:outline-none focus:border-primary appearance-none rounded-none font-mono"
        >
          <option value="">Auto — best available account</option>
          {accounts.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
        </select>
        <p className="text-xs text-slate-500 mt-2">
          {selectedAccountId
            ? 'Deployments will be pinned to this account only.'
            : 'Leave on Auto to let the system pick the account/region with free capacity.'}
        </p>
      </div>
      
      <div className="flex gap-4 border-b border-white/10 pb-4">
        <button 
          onClick={() => setActiveTab('direct')} 
          className={`font-bold px-4 py-2 uppercase tracking-wider transition-colors ${activeTab === 'direct' ? 'bg-primary text-black' : 'text-slate-400 hover:text-white'}`}
        >
          Direct Zip Upload
        </button>
        <button 
          onClick={() => setActiveTab('saved')} 
          className={`font-bold px-4 py-2 uppercase tracking-wider transition-colors ${activeTab === 'saved' ? 'bg-primary text-black' : 'text-slate-400 hover:text-white'}`}
        >
          Deploy from Saved Sites
        </button>
      </div>

      {activeTab === 'saved' ? (
        <div className="space-y-8">
          <div className="bg-card border border-white/10 p-6 max-w-xl space-y-6">
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 font-bold block mb-2">Select Saved Site</label>
              <select 
                value={selectedSiteId} 
                onChange={(e) => setSelectedSiteId(e.target.value)}
                className="w-full bg-[#0A0B0E] border border-white/10 p-3 text-white focus:outline-none focus:border-primary appearance-none rounded-none font-mono"
              >
                <option value="">-- Choose an asset --</option>
                {savedSites.map(s => <option key={s.id} value={s.id}>{s.name} {s.type === 'git' ? '(Git)' : `(${((s.size_bytes||0)/1024/1024).toFixed(1)}MB)`}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 font-bold block mb-2">Number of Copies / URLs</label>
              <input 
                type="number" min="1" max="50"
                value={savedDeployCount}
                onChange={(e) => setSavedDeployCount(parseInt(e.target.value) || 1)}
                className="w-full bg-[#0A0B0E] border border-white/10 p-3 font-mono text-xl focus:outline-none focus:border-primary"
              />
            </div>
            <button 
              onClick={handleDeploySaved}
              disabled={deployingSaved || !selectedSiteId}
              className="w-full bg-primary text-black px-8 py-4 font-black uppercase tracking-wider hover:bg-[#00B8CC] transition-colors disabled:opacity-50"
            >
              LAUNCH {savedDeployCount} SITES
            </button>
          </div>

          {savedDeployments.length > 0 && (
            <div className="bg-card border border-white/10 overflow-hidden">
              <div className="p-4 border-b border-white/10 bg-black/20 flex justify-between items-center">
                <h3 className="font-bold uppercase tracking-wider">Deployment Results</h3>
                {!deployingSaved && (
                  <button 
                    data-testid="do-saved-copy-all-btn"
                    onClick={() => handleCopyAll(savedDeployments.map(d => ({ status: d.status, url: d.url, label: d.bucket_name })))}
                    className="bg-[#0A0B0E] border border-white/20 text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-white/10 transition-colors flex items-center gap-2"
                  >
                    <Copy size={16} /> Copy All
                  </button>
                )}
              </div>
              <table className="w-full text-left text-sm border-collapse">
                <tbody>
                  {savedDeployments.map((dep, i) => (
                    <tr key={i} className="border-b border-white/10">
                      <td className="p-4 font-mono font-bold">{dep.bucket_name}</td>
                      <td className="p-4">{getStatusDisplay(dep.status)}</td>
                      <td className="p-4 font-mono">
                        {dep.status === 'live' && (() => {
                          let finalUrl = dep.url;
                          if (!finalUrl.startsWith('http')) finalUrl = 'https://' + finalUrl;
                          finalUrl = finalUrl.endsWith('/') ? finalUrl + 'index.html' : (finalUrl.endsWith('/index.html') ? finalUrl : finalUrl + '/index.html');
                          return (
                            <div className="flex items-center gap-2">
                              <div className="bg-[#0A0B0E] border border-white/10 px-2 py-1 text-xs text-slate-400 font-mono truncate w-48 select-all" title={finalUrl}>
                                {finalUrl}
                              </div>
                              <button onClick={() => { navigator.clipboard.writeText(finalUrl); toast.success("Copied!"); }} className="text-slate-400 hover:text-white transition-colors p-1" title="Copy URL"><Copy size={16} /></button>
                              <a href={finalUrl} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-primary transition-colors p-1" title="Preview Site"><Eye size={16} /></a>
                            </div>
                          );
                        })()}
                        {dep.status === 'failed' && <span className="text-red-500 text-xs">{dep.error}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-8">
          <div className="bg-card border border-white/10 p-6 max-w-xl">
            <label className="text-xs uppercase tracking-widest text-slate-500 font-bold block mb-2">Number of Sites / URLs</label>
            <input 
              type="number" min="1" max="20"
              value={linksCount}
              onChange={(e) => setLinksCount(parseInt(e.target.value) || 1)}
              className="w-full bg-[#0A0B0E] border border-white/10 p-3 font-mono text-xl focus:outline-none focus:border-primary"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {slots.map((slot, i) => (
              <div key={i} className={`border ${slot.status === 'live' ? 'border-[#39FF14]/50' : slot.status === 'failed' ? 'border-red-500/50' : 'border-white/10 border-dashed'} bg-card p-6 flex flex-col min-h-[220px] relative transition-colors`}>
                {slot.status === 'idle' && (
                  <input type="file" accept=".zip" onChange={(e) => handleFileChange(i, e.target.files[0])} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" />
                )}

                <div className="flex-1 flex flex-col items-center justify-center text-center">
                  {slot.file ? (
                    <>
                      <FileZip size={48} className={`mb-2 ${slot.status === 'live' ? 'text-[#39FF14]' : slot.status === 'failed' ? 'text-red-500' : 'text-primary'}`} />
                      <p className="font-mono text-sm font-bold text-white truncate max-w-[200px] w-full">{slot.file.name}</p>
                      <p className="text-xs text-slate-500 mt-1">{(slot.file.size / 1024 / 1024).toFixed(2)} MB</p>
                    </>
                  ) : (
                    <div className="text-slate-500">
                      <UploadSimple size={48} className="mx-auto mb-2 opacity-50" />
                      <p className="font-bold text-sm text-white">Click or Drag Zip</p>
                      <p className="text-xs mt-1">Slot #{i + 1}</p>
                    </div>
                  )}
                </div>

                <div className="mt-4 h-8 flex items-center justify-between border-t border-white/10 pt-4">
                  {getStatusDisplay(slot.status)}
                  {slot.status === 'idle' && slot.file && <button onClick={() => handleFileChange(i, null)} className="text-xs text-slate-500 hover:text-red-400 uppercase tracking-wider relative z-20">Remove</button>}
                  {slot.status === 'live' && slot.url && (() => {
                    let finalUrl = slot.url;
                    if (!finalUrl.startsWith('http')) finalUrl = 'https://' + finalUrl;
                    finalUrl = finalUrl.endsWith('/') ? finalUrl + 'index.html' : (finalUrl.endsWith('/index.html') ? finalUrl : finalUrl + '/index.html');
                    return (
                      <div className="flex items-center gap-2 relative z-20 bg-[#0A0B0E] border border-white/10 p-1">
                        <div className="text-[10px] text-slate-400 font-mono truncate w-24 select-all" title={finalUrl}>
                          {finalUrl}
                        </div>
                        <button onClick={() => { navigator.clipboard.writeText(finalUrl); toast.success("Copied!"); }} className="text-slate-400 hover:text-white transition-colors"><Copy size={14} /></button>
                        <a href={finalUrl} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-primary transition-colors"><Eye size={14} /></a>
                      </div>
                    );
                  })()}
                </div>

                {['uploading', 'extracting'].includes(slot.status) && (
                  <div className="absolute bottom-0 left-0 h-1 bg-primary/20 w-full">
                    <div className="h-full bg-primary transition-all duration-300 ease-out" style={{ width: `${slot.progress}%` }} />
                  </div>
                )}

                {slot.error && (
                  <div className="absolute bottom-0 left-0 w-full bg-red-500/10 text-red-500 text-[10px] p-2 truncate border-t border-red-500/20 font-mono">
                    {slot.error}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="pt-4 border-t border-white/10 flex items-center justify-between">
            <button 
              onClick={handleDeployDirect}
              disabled={deployingDirect || slots.every(s => s.status === 'live' || s.status === 'failed' && s.file === null)}
              className="bg-primary text-black px-8 py-4 font-black uppercase tracking-wider hover:bg-[#00B8CC] transition-colors disabled:opacity-50"
            >
              START DIRECT UPLOAD BATCH
            </button>
          </div>
        </div>
      )}

      {/* Completion Modal */}
      {showCompletionModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="do-completion-modal">
          <div className="bg-card border border-primary/50 p-8 w-full max-w-2xl relative animate-in zoom-in-95 duration-200 shadow-[0_0_20px_rgba(0,229,255,0.2)]">
            <button onClick={() => setShowCompletionModal(false)} className="absolute top-4 right-4 text-slate-400 hover:text-white" data-testid="do-modal-close-btn">
              <X size={24} />
            </button>
            <h2 className="text-2xl font-bold mb-2 text-primary">DEPLOYMENT BATCH COMPLETE</h2>
            <p className="text-slate-400 mb-6">Your requested URLs have finished deploying.</p>
            
            <div className="bg-[#0A0B0E] border border-white/10 p-4 max-h-[300px] overflow-y-auto mb-6 font-mono text-sm space-y-1">
              {modalDeps.map((dep, i) => (
                <div key={i} className="flex items-center gap-2">
                  {dep.status === 'live' && dep.url ? (
                    <span className="text-[#39FF14] break-all">{formatDoUrl(dep.url)}</span>
                  ) : (
                    <span className="text-red-500 line-through">{dep.label || 'Site'} (Failed)</span>
                  )}
                </div>
              ))}
            </div>

            <div className="flex gap-4">
              <button 
                data-testid="do-modal-copy-btn"
                onClick={() => handleCopyAll()}
                className="flex-1 bg-primary text-black py-3 font-bold uppercase tracking-wider hover:bg-[#00B8CC] transition-colors flex items-center justify-center gap-2"
              >
                <Copy size={20} /> Copy Live URLs
              </button>
              <button 
                onClick={() => setShowCompletionModal(false)}
                className="flex-1 bg-transparent border border-white/20 text-white py-3 font-bold uppercase tracking-wider hover:bg-white/10 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewDeployment;